import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { STAGE_LABELS, usd, fmtDate } from "@/lib/format";
import { LayoutGrid, Table2, Plus, Phone, MapPin, X } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";

const STAGES = ["new", "qualified", "appointment_set", "measured", "estimated", "proposal_sent", "signed", "in_production", "complete"];

function NewLeadModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "", lead_source: "Manual" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    setSaving(true);
    try {
      const c = await api.post("/contacts", f);
      if (c.data.duplicate_warning) toast.warning(`Possible duplicate: ${c.data.duplicate_warning}`);
      await api.post("/leads", { contact_id: c.data.id, lead_source: f.lead_source, status: "new" });
      toast.success("Lead created"); onCreated();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="mb-card p-6 w-full max-w-md animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="new-lead-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl font-bold uppercase text-navy">New Lead</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {["name", "phone", "email"].map((k) => (
          <div key={k} className="mb-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{k}</label>
            <input data-testid={`lead-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
          </div>
        ))}
        <div className="mb-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">address</label>
          <AddressAutocomplete testid="lead-address" value={f.address} onChange={(v) => setF({ ...f, address: v })}
            className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
        </div>
        <Btn variant="red" onClick={save} disabled={saving} className="w-full mt-2" testid="save-lead"><Plus className="w-4 h-4" /> Create Lead</Btn>
      </div>
    </div>
  );
}

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("kanban");
  const [modal, setModal] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const move = async (lead, status) => {
    await api.put(`/leads/${lead.id}/status?status=${status}`);
    load();
  };

  const byStage = (s) => leads.filter((l) => l.status === s);

  return (
    <div>
      <PageHeader title="CRM Pipeline" subtitle="One customer record from lead to referral — drag to advance"
        actions={
          <div className="flex bg-white border-2 border-slate-200 rounded-xl overflow-hidden">
            <button data-testid="view-kanban" onClick={() => setView("kanban")} className={`px-3 py-2 ${view === "kanban" ? "bg-navy text-white" : "text-slate-500"}`}><LayoutGrid className="w-4 h-4" /></button>
            <button data-testid="view-table" onClick={() => setView("table")} className={`px-3 py-2 ${view === "table" ? "bg-navy text-white" : "text-slate-500"}`}><Table2 className="w-4 h-4" /></button>
          </div>
        }
        leftActions={
          <Btn variant="red" onClick={() => setModal(true)} testid="new-lead-btn"><Plus className="w-4 h-4" /> New Lead</Btn>
        } />

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto mb-scroll pb-4">
          {STAGES.map((s) => (
            <div key={s} className="min-w-[280px] w-[280px] shrink-0" data-testid={`column-${s}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { const id = e.dataTransfer.getData("lead"); const l = leads.find((x) => x.id === id); if (l && l.status !== s) move(l, s); }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-display font-bold uppercase text-sm text-navy">{STAGE_LABELS[s]}</span>
                <span className="font-num text-xs bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">{byStage(s).length}</span>
              </div>
              <div className="space-y-2">
                {byStage(s).map((l) => (
                  <div key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("lead", l.id)}
                    data-testid={`lead-card-${l.id}`} onClick={() => nav(`/crm/contact/${l.contact_id}`)}
                    className="mb-card p-3 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="flex justify-between items-start">
                      <div className="font-semibold text-navy text-sm">{l.contact?.name || "Unknown"}</div>
                      {!l.last_contacted && l.status === "new" && <Pill tone="danger">Follow up</Pill>}
                    </div>
                    {l.contact?.phone && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {l.contact.phone}</div>}
                    {l.contact?.address && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate"><MapPin className="w-3 h-3 shrink-0" /> {l.contact.address}</div>}
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{l.lead_source}</span>
                      {l.value_cents ? <span className="font-num text-xs text-successg font-bold">{usd(l.value_cents)}</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Created</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => nav(`/crm/contact/${l.contact_id}`)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-navy">{l.contact?.name}</td>
                  <td className="px-4 py-3 text-slate-600">{l.contact?.phone}</td>
                  <td className="px-4 py-3"><Pill tone="navy">{STAGE_LABELS[l.status]}</Pill></td>
                  <td className="px-4 py-3 text-slate-500">{l.lead_source}</td>
                  <td className="px-4 py-3 font-num text-successg">{l.value_cents ? usd(l.value_cents) : "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && <NewLeadModal onClose={() => setModal(false)} onCreated={() => { setModal(false); load(); }} />}
    </div>
  );
}
